import { Component, OnInit, ViewChild } from '@angular/core';
import { ICustomer } from '../../interfaces/customer';
import { FormControl, FormGroup } from '@angular/forms';
import { OrganisationsService, UserService } from '../../services';
import {
  ICustomerDepartmentPriority,
  ICustomerDepartment,
  ICustomerPriority,
  ICustomerPriorityCreate,
  IDepartment,
} from '../../interfaces/department';
import { forkJoin } from 'rxjs';
import { OrganisationTypeEnum } from '../../enums/organisation-types';
import { CustomerPriorityService } from '../../services/customer-priority.service';
import { compare } from 'fast-json-patch';
import { AMPriorityEnum } from '../../enums/am-priority.enum';
import * as moment from 'moment';
import { Table } from 'primeng';

@Component({
  selector: 'customer-priority',
  templateUrl: './customer-priority.component.html',
  styleUrls: ['./customer-priority.component.scss'],
})
export class CustomerPriorityComponent implements OnInit {
  @ViewChild('priorityTable') priorityTable: Table;

  customers: ICustomer[] = [];
  updateByUserOptions: { value: string; label: string }[] = [];
  pmChecksOptions: { value: boolean; label: string }[] = [];
  amPriorityOptions: { value: number; label: string }[] = [];
  inlinePriorityOptions: { value: number; label: string }[] = [];
  departmentOptions: { value: number; label: string; customerId: number }[] = [];
  teamOptions: {
    value: number;
    label: string;
    customerId: number;
    customerName: string;
    departmentId: number;
    departmentName: string;
  }[] = [];
  customerOptions: { value: number; label: string }[] = [];

  filterForm: FormGroup = new FormGroup({
    customerId: new FormControl([null]),
    departmentId: new FormControl([null]),
    teamId: new FormControl([null]),
    amPriorities: new FormControl([null]),
    pmChecks: new FormControl([null]),
    updatedByUsers: new FormControl([null]),
    showTeams: new FormControl(false),
  });

  private departments: IDepartment[];
  customerDepartmentList: ICustomerDepartmentPriority[] = [];
  beforeEdit: ICustomerDepartmentPriority[] = [];
  sortedFilteredCustomerDepartmentList: ICustomerDepartmentPriority[] = [];
  showInfoDialog: boolean = false;
  showTeams: boolean = false;

  constructor(
    private customerService: OrganisationsService,
    private customerPriorityService: CustomerPriorityService,
    private userService: UserService,
  ) {
    this.inlinePriorityOptions = [
      { value: 0, label: 'Unset Priority' },
      { value: 1, label: 'W1 Critical Priority' },
      { value: 2, label: 'W2 High Priority' },
      { value: 3, label: 'W3 Medium Priority' },
      { value: 4, label: 'W4 Low Priority' },
    ];

    this.amPriorityOptions = [...this.inlinePriorityOptions];
    this.amPriorityOptions.unshift({ value: null, label: 'All AM Priorities' });

    this.pmChecksOptions = [
      { value: null, label: 'All PM Checks' },
      { value: true, label: 'PM Checks Required' },
      { value: false, label: 'PM Check Not Required' },
    ];

    this.filterForm.valueChanges.subscribe(() => {
      this.filterResults();
    });
  }

  async ngOnInit() {
    const getCustomers = this.customerService.getAllCustomers().then((customers) => {
      this.customerOptions = customers.map((customer) => {
        return { value: customer.id, label: customer.name };
      });
      this.customerOptions.unshift({ value: null, label: 'All Customers' });
    });

    const getDepartments = this.customerService
      .getAllDepartments()
      .then((departments: IDepartment[]) => {
        this.departments = departments;
      });

    this.fetchCustomerPriorities();

    this.userService
      .getUsersWithRoles({
        filters: {
          responseType: { value: 'key-value' },
          organisationTypeId: { value: OrganisationTypeEnum.Engineius },
        },
      })
      .then((userPage) => {
        const users = userPage.items;
        this.updateByUserOptions = users.map((user) => {
          return { value: user.id, label: `${user.firstName} ${user.lastName}` };
        });

        this.updateByUserOptions = this.updateByUserOptions.sort((a, b) => {
          return a.label.localeCompare(b.label);
        });

        this.updateByUserOptions.unshift({ value: null, label: 'Updated By (All Users)' });
      });

    forkJoin([getCustomers, getDepartments]).subscribe(() => {
      this.departmentOptions = this.departments.map((department) => {
        return {
          value: department.id,
          label: department.name,
          customerId: department.customerId,
          customerName: this.customerOptions.find((x) => x.value == department.customerId)?.label,
        };
      });
      this.departmentOptions.unshift({ value: null, label: 'All Departments', customerId: null });
    });
  }

  filteredDepartments(departmentOptions: { value: number; label: string }[]) {
    let options = this.departmentOptions.filter((department) => {
      return (
        this.filterForm.get('customerId').value.includes(department.customerId) ||
        department.customerId === null ||
        this.filterForm.get('customerId').value[0] === null
      );
    });
    return options;
  }

  filteredTeams(teamOptions: { value: number; label: string }[]) {
    let options = this.teamOptions.filter((team) => {
      return (
        this.filterForm.get('customerId').value.includes(team.customerId) ||
        team.customerId === null ||
        this.filterForm.get('customerId').value[0] === null
      );
    });
    return options;
  }

  get f() {
    return this.filterForm.controls;
  }

  fetchCustomerPriorities() {
    const customers = this.customerService
      .getAllCustomerDepartmentPriorities()
      .then((response: ICustomerDepartment[]) => {
        this.customerDepartmentList = [];

        response.forEach((customer) => {
          customer.departments.forEach((department) => {
            department.teams.forEach((team) => {
              this.customerDepartmentList.push({
                customerId: customer.id,
                departmentId: department?.id,
                departmentName: department?.name,
                teamId: team?.id,
                teamName: team?.name,
                customerName: customer.name,
                customerPriorityId: team?.customerPriority?.id,
                updated: team?.customerPriority?.updated,
                userId: team?.customerPriority?.userId,
                amCheckPriority: team?.customerPriority?.amCheckPriority || AMPriorityEnum.Unset,
                pmCheckRequired: team?.customerPriority?.pmCheckRequired,
              });
              this.teamOptions.push({
                value: team.id,
                label: team.name,
                customerId: customer.id,
                departmentId: department?.id,
                customerName: this.customerOptions.find((x) => x.value == customer.id)?.label,
                departmentName: department?.name,
              });
            });
            this.customerDepartmentList.push({
              customerId: customer.id,
              departmentId: department?.id,
              departmentName: department?.name,
              customerName: customer.name,
              customerPriorityId: department?.customerPriority?.id,
              updated: department?.customerPriority?.updated,
              userId: department?.customerPriority?.userId,
              amCheckPriority:
                department?.customerPriority?.amCheckPriority || AMPriorityEnum.Unset,
              pmCheckRequired: department?.customerPriority?.pmCheckRequired,
            });
          });
        });
        this.defaultSort(this.customerDepartmentList);

        this.teamOptions.unshift({
          value: null,
          label: 'All Teams',
          customerId: null,
          customerName: null,
          departmentId: null,
          departmentName: null,
        });
        this.customerSnapshot();
      });
  }

  customerSnapshot() {
    this.beforeEdit = JSON.parse(JSON.stringify(this.customerDepartmentList));
    this.filterResults();
  }

  changeCustomerList() {
    this.filterChanged('customerId');
  }

  changedAMPriority() {
    this.filterChanged('amPriorities');
  }

  changedDepartments() {
    this.filterChanged('departmentId');
  }

  changedPMChecks() {
    this.filterChanged('pmChecks');
  }

  changedUpdateByUsers() {
    this.filterChanged('updatedByUsers');
  }

  filterChanged(filterName: string) {
    let items = this.f[filterName].value;

    const oneSelected = items.some((item) => item !== null);

    if (items[items.length - 1] == null) {
      this.filterForm.patchValue({ [filterName]: [null] });
    } else if (oneSelected) {
      this.filterForm.patchValue({ [filterName]: items.filter((x) => x != null) });
    } else if (items.length == 0) {
      this.filterForm.patchValue({ [filterName]: [null] });
    }
    this.priorityTable.reset();
  }

  resetForm() {
    this.filterForm.patchValue({
      customerId: [null],
      departmentId: [null],
      teamId: [null],
      amPriorities: [null],
      pmChecks: [null],
      updatedByUsers: [null],
      showTeams: false,
    });

    this.priorityTable.reset();

    this.filterResults();
  }

  filterResults() {
    const filter = this.filterForm.value;

    this.sortedFilteredCustomerDepartmentList = this.customerDepartmentList.filter((x) => {
      return (
        (filter.customerId?.includes(x.customerId) || filter.customerId[0] === null) &&
        (filter.departmentId?.includes(x.departmentId) || filter.departmentId[0] === null) &&
        (filter.teamId?.includes(x.teamId) || filter.teamId[0] === null) &&
        (filter.amPriorities?.includes(x.amCheckPriority) || filter.amPriorities[0] === null) &&
        (filter.pmChecks?.includes(x.pmCheckRequired) ||
          (filter.pmChecks[0] == false && x.pmCheckRequired == null) ||
          filter.pmChecks[0] === null) &&
        (filter.updatedByUsers?.includes(x.userId) || filter.updatedByUsers[0] === null) &&
        (filter.showTeams || x.teamId == null)
      );
    });
  }

  setValue(item: ICustomerDepartmentPriority, parameter: string) {
    item.userId = this.userService.currentUser.id;
    item.updated = new Date().toDateString();

    const dto: ICustomerPriorityCreate = {
      customerId: item.customerId,
      departmentId: item.departmentId,
      teamId: item.teamId,
      amCheckPriority: item.amCheckPriority,
      pmCheckRequired: item.pmCheckRequired,
    };

    this.customerPriorityService.upsert(dto).then((customerPriority: ICustomerPriority) => {
      item.customerPriorityId = customerPriority.id;
      this.customerSnapshot();
    });
  }

  userLookup(userId: string) {
    if (userId == null) {
      return null;
    }
    return this.updateByUserOptions.find((x) => x.value == userId)?.label;
  }

  private defaultSort(customerDepartmentList: ICustomerDepartmentPriority[]) {
    // Sort by apcheckpriority then by pmcheckpriority

    customerDepartmentList.sort((a, b) => {
      // First compare by amCheckPriority
      if (a.amCheckPriority !== b.amCheckPriority) {
        return a.amCheckPriority - b.amCheckPriority;
      }
      // If amCheckPriority is equal, then sort by pmCheckPriority
      return Number(b.pmCheckRequired) - Number(a.pmCheckRequired);
    });
  }

  showCRM(id: number) {
    window.open(`/app/customer-information/${id}`, '_blank');
  }

  changedTeams() {
    this.filterChanged('teamId');
  }

  changedShowTeams() {
    this.showTeams = this.f['showTeams'].value;
    this.priorityTable.reset();
  }
}
